/**
 * CAT Filters Component
 * Inline filter bar: filters stay visible and apply live (no popover).
 */

import React from 'react';
import { TextField } from '@mui/material';
import { InlineFilterBar, InlineMultiSelect, InlineSelect } from '../../../components/common';
import { useFilterState } from '../../../hooks/useFilterState';
import {
  CatStatus,
  CatReportType,
  AccidentType,
  CAT_STATUS_LABELS,
  CAT_REPORT_TYPE_LABELS,
  ACCIDENT_TYPE_LABELS,
} from '../../../types/cat';

export interface CatFiltersData {
  search: string;
  status: CatStatus[];
  reportType: CatReportType | '';
  accidentType: AccidentType | '';
  startDate: string;
  endDate: string;
}

interface CatFiltersProps {
  onFilter: (filters: CatFiltersData) => void;
  loading?: boolean;
  total?: number;
}

const STATUS_OPTIONS = Object.values(CatStatus).map((s) => ({
  value: s,
  label: CAT_STATUS_LABELS[s],
}));
const REPORT_TYPE_OPTIONS = Object.values(CatReportType).map((t) => ({
  value: t,
  label: CAT_REPORT_TYPE_LABELS[t],
}));
const ACCIDENT_TYPE_OPTIONS = Object.values(AccidentType).map((t) => ({
  value: t,
  label: ACCIDENT_TYPE_LABELS[t],
}));

const defaultValues: CatFiltersData = {
  search: '',
  status: [],
  reportType: '',
  accidentType: '',
  startDate: '',
  endDate: '',
};

export const CatFilters: React.FC<CatFiltersProps> = ({ onFilter, loading = false, total }) => {
  const { filters, setFilter, clearAll, activeChips } = useFilterState(defaultValues, {
    onFilter,
    applyOnMount: true,
    live: true,
    labels: {
      status: 'Status',
      reportType: 'Tipo de CAT',
      accidentType: 'Tipo de Acidente',
      startDate: 'Data Inicial',
      endDate: 'Data Final',
    },
    formatValue: {
      status: (v: CatStatus[]) => v.map((s) => CAT_STATUS_LABELS[s] ?? s).join(', '),
      reportType: (v) => CAT_REPORT_TYPE_LABELS[v as CatReportType] ?? v,
      accidentType: (v) => ACCIDENT_TYPE_LABELS[v as AccidentType] ?? v,
    },
  });

  return (
    <InlineFilterBar
      searchPlaceholder="Buscar por funcionário, CPF ou situação..."
      searchValue={filters.search}
      onSearchChange={(v) => setFilter('search', v)}
      resultCount={total}
      resultNoun="CATs"
      chips={activeChips}
      onClearAll={clearAll}
      loading={loading}
    >
      <InlineMultiSelect
        label="Status"
        value={filters.status}
        options={STATUS_OPTIONS}
        onChange={(v) => setFilter('status', v as CatStatus[])}
        minWidth={140}
      />
      <InlineSelect
        label="Tipo de CAT"
        value={filters.reportType}
        options={REPORT_TYPE_OPTIONS}
        onChange={(v) => setFilter('reportType', v as CatReportType | '')}
        emptyLabel="Todos"
        minWidth={150}
      />
      <InlineSelect
        label="Tipo de Acidente"
        value={filters.accidentType}
        options={ACCIDENT_TYPE_OPTIONS}
        onChange={(v) => setFilter('accidentType', v as AccidentType | '')}
        emptyLabel="Todos"
        minWidth={160}
      />
      <TextField
        size="small"
        type="date"
        label="Data Inicial"
        value={filters.startDate}
        onChange={(e) => setFilter('startDate', e.target.value)}
        InputLabelProps={{ shrink: true }}
        sx={{ minWidth: 160 }}
      />
      <TextField
        size="small"
        type="date"
        label="Data Final"
        value={filters.endDate}
        onChange={(e) => setFilter('endDate', e.target.value)}
        InputLabelProps={{ shrink: true }}
        sx={{ minWidth: 160 }}
      />
    </InlineFilterBar>
  );
};

export default CatFilters;
